class SiteHeader {
  constructor(root) {
    this.root = root;
    this.lastScrollY = window.scrollY;
    this.ticking = false;
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.logoAnimationDistance = 150;
    this.isTransparent = root.dataset.transparent === 'true';
    this.remSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    this.cachedStickyHeight = null;

    this.toggle = root.querySelector('[data-header-toggle]');
    this.closeButton = root.querySelector('[data-header-close]');
    this.drawer = root.querySelector('[data-header-drawer]');
    this.overlay = root.querySelector('[data-header-overlay]');
    this.panel = root.querySelector('[data-header-panel]');
    this.logo = root.querySelector('.header-logo-svg--animated');
    this.spacer = root.nextElementSibling?.matches('[data-header-spacer]')
      ? root.nextElementSibling
      : null;
    this.focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    this.onScroll = this.onScroll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);

    this.init();
  }

  init() {
    this.syncHeaderOffset();
    this.syncStickyHeaderOffset();
    if ('ResizeObserver' in window) {
      this.headerResizeObserver = new ResizeObserver(() => {
        this.cachedStickyHeight = null;
        this.syncHeaderOffset();
        this.syncStickyHeaderOffset();
      });
      this.headerResizeObserver.observe(this.root);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.updateState();

    this.desktopMedia.addEventListener('change', this.onDesktopChange);
    this.reducedMotionMedia.addEventListener('change', this.onMotionChange);
    this.toggle?.addEventListener('click', () => this.toggleDrawer());
    this.closeButton?.addEventListener('click', () => this.closeDrawer());
    this.overlay?.addEventListener('click', () => this.closeDrawer());
    document.addEventListener('keydown', this.onKeyDown);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateState();
      this.ticking = false;
    });
  }

  onDesktopChange() {
    this.updateState();
  }

  onMotionChange() {
    this.updateState();
  }

  syncHeaderOffset() {
    if (this.root.classList.contains('site-header--compact')) return;

    const offset = `${this.root.offsetHeight}px`;
    document.documentElement.style.setProperty('--header-offset', offset);

    if (this.spacer) {
      this.spacer.style.setProperty('--header-offset', offset);
    }
  }

  /**
   * Compact/fixed header height for scroll targets (distinct from
   * --header-offset, which stays at the tall transparent size for spacers).
   */
  syncStickyHeaderOffset() {
    const height = this.getStickyHeaderHeight();
    document.documentElement.style.setProperty(
      '--header-sticky-offset',
      `${height}px`
    );
  }

  getStickyHeaderHeight() {
    if (
      this.root.classList.contains('site-header--compact') ||
      this.root.classList.contains('site-header--solid')
    ) {
      const live = Math.ceil(this.root.getBoundingClientRect().height);
      this.cachedStickyHeight = live;
      return live;
    }

    if (this.cachedStickyHeight) {
      return this.cachedStickyHeight;
    }

    const probe = this.root.cloneNode(true);
    probe.classList.add('site-header--compact', 'site-header--pinned');
    probe.classList.remove('site-header--hidden', 'site-header--absolute');
    probe.removeAttribute('id');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;top:0;left:0;right:0;visibility:hidden;pointer-events:none;z-index:-1;';
    document.body.appendChild(probe);
    const height = Math.ceil(probe.getBoundingClientRect().height);
    probe.remove();

    this.cachedStickyHeight = height > 0 ? height : Math.round(4.25 * this.remSize);
    return this.cachedStickyHeight;
  }

  updateState() {
    const scrollY = window.scrollY;
    const isDesktop = this.desktopMedia.matches;
    const logoComplete = scrollY >= this.logoAnimationDistance;
    const shouldPin = this.isTransparent && scrollY > 0;
    const wasCompact = this.root.classList.contains('site-header--compact');

    // Pin transparent headers while the logo shrinks; wait for the A to
    // reach its final size before compact + background fade in.
    this.root.classList.toggle('site-header--pinned', shouldPin);
    this.root.classList.toggle('site-header--compact', logoComplete);
    this.updateLogo(scrollY);

    if (logoComplete !== wasCompact) {
      this.syncStickyHeaderOffset();
    }

    if (isDesktop) {
      this.root.classList.remove('site-header--hidden');
    } else {
      const scrollingDown = scrollY > this.lastScrollY;
      const pastThreshold = scrollY > this.logoAnimationDistance;

      if (pastThreshold && scrollingDown) {
        this.root.classList.add('site-header--hidden');
      } else {
        this.root.classList.remove('site-header--hidden');
      }
    }

    this.lastScrollY = scrollY;
  }

  updateLogo(scrollY) {
    if (!this.logo) return;

    const rawProgress = Math.min(1, Math.max(0, scrollY / this.logoAnimationDistance));
    const progress = this.reducedMotionMedia.matches
      ? Number(scrollY >= this.logoAnimationDistance)
      : rawProgress * rawProgress * (3 - 2 * rawProgress);
    const startWidth = (this.isTransparent && this.desktopMedia.matches ? 11 : 6) * this.remSize;
    const startHeight = startWidth * (153 / 180);
    const endSize = 3.5 * this.remSize;
    const width = startWidth + (endSize - startWidth) * progress;
    const height = startHeight + (endSize - startHeight) * progress;
    const markWidthRatio = 34.8285 / 180;
    const startMarkWidth = startWidth * markWidthRatio;
    const endMarkWidth = 2.5 * this.remSize;
    const visibleMarkWidth = startMarkWidth + (endMarkWidth - startMarkWidth) * progress;
    const markScale = visibleMarkWidth / (width * markWidthRatio);

    this.logo.style.setProperty('--header-logo-width', `${Math.round(width)}px`);
    this.logo.style.setProperty('--header-logo-height', `${Math.round(height)}px`);
    this.logo.style.setProperty('--header-logo-artwork-opacity', (1 - progress).toFixed(4));
    this.logo.style.setProperty(
      '--header-logo-mark-translate-x',
      `${(36.56 * progress).toFixed(3)}%`
    );
    this.logo.style.setProperty(
      '--header-logo-mark-translate-y',
      `${(9.22 * progress).toFixed(3)}%`
    );
    this.logo.style.setProperty('--header-logo-mark-scale', markScale.toFixed(4));
  }

  toggleDrawer() {
    if (this.drawer?.classList.contains('is-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    if (!this.drawer || !this.toggle) return;

    this.drawer.hidden = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.labelClose || 'Close menu');
    this.root.classList.add('site-header--drawer-open');
    document.documentElement.classList.add('site-header-drawer-open');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
      });
    });

    this.focusableElements = Array.from(
      this.panel.querySelectorAll(this.focusableSelector)
    );
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    this.closeButton?.focus();
  }

  closeDrawer() {
    if (!this.drawer || !this.toggle) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.labelOpen || 'Open menu');
    this.root.classList.remove('site-header--drawer-open');
    document.documentElement.classList.remove('site-header-drawer-open');

    const finishClose = () => {
      this.panel.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallbackTimer);
      this.drawer.hidden = true;
    };

    const onTransitionEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'transform') return;
      finishClose();
    };

    const fallbackTimer = window.setTimeout(finishClose, 400);
    this.panel.addEventListener('transitionend', onTransitionEnd);
    this.toggle.focus();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (event.key !== 'Tab' || !this.drawer?.classList.contains('is-open')) return;

    if (event.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        event.preventDefault();
        this.lastFocusable?.focus();
      }
    } else if (document.activeElement === this.lastFocusable) {
      event.preventDefault();
      this.firstFocusable?.focus();
    }
  }

  destroy() {
    this.headerResizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    this.desktopMedia.removeEventListener('change', this.onDesktopChange);
    this.reducedMotionMedia.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('keydown', this.onKeyDown);
  }
}

function initHeaders(scope = document) {
  scope.querySelectorAll('[data-header]').forEach((element) => {
    if (element._siteHeader) element._siteHeader.destroy();
    element._siteHeader = new SiteHeader(element);
  });
}

document.addEventListener('DOMContentLoaded', () => initHeaders());

document.addEventListener('shopify:section:load', (event) => {
  initHeaders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-header]').forEach((element) => {
    element._siteHeader?.destroy();
    element._siteHeader = null;
  });
});
